import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./productlayout.css";
import { ToastContainer } from "react-toastify";
import { CiFilter } from "react-icons/ci";
import { HiOutlineShoppingBag } from "react-icons/hi2";
import { useSelector, useDispatch } from "react-redux";
import { addToCart } from "../store/action/cartAction";
import Cart from "../checkout/Cart";
import {
  setCurrentPage,
  setSelectedCategory,
} from "../store/action/filterAction";
import { ensureShuffledOrder } from "../store/action/shuffleAction";

// const serverUrl = process.env.REACT_APP_SERVER_URL;

// ─── SEARCH COMPONENTS ─────────────────────────────────────────

const SearchIcon = ({ focused }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition:
        "transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.3s ease",
      transform: focused
        ? "scale(1.15) rotate(-8deg)"
        : "scale(1) rotate(0deg)",
      color: focused ? "#a9a9a9" : "#64748b",
    }}
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

function SearchBar({ searchTerm, setSearchTerm }) {
  const [focused, setFocused] = useState(false);

  return (
    <div className={`search-wrapper ${focused ? "focused" : ""}`}>
      <div className="icon-wrap">
        <SearchIcon focused={focused} />
      </div>

      <input
        className="search-input"
        type="text"
        placeholder="Search products..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />

      <button
        className={`clear-btn ${searchTerm ? "visible" : ""}`}
        onClick={() => setSearchTerm("")}
        tabIndex={-1}
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          stroke="#64748b"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <line x1="1" y1="1" x2="9" y2="9" />
          <line x1="9" y1="1" x2="1" y2="9" />
        </svg>
      </button>

      <div className="underline-bar" />
    </div>
  );
}

// ─── Pagination Sub-components ─────────────────────────────────

function PageBtn({ page, active, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`page-btn ${active ? "active" : ""}`}
    >
      {page}
    </button>
  );
}

function NavBtn({ onClick, disabled, label, title }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="nav-btn"
    >
      {label}
    </button>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  const getPages = () => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages = [];
    pages.push(1);

    if (currentPage > 3) pages.push("…");

    for (
      let p = Math.max(2, currentPage - 1);
      p <= Math.min(totalPages - 1, currentPage + 1);
      p++
    ) {
      pages.push(p);
    }

    if (currentPage < totalPages - 2) pages.push("…");

    pages.push(totalPages);

    return pages;
  };

  return (
    <div className="pagination">
      <NavBtn
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        label="‹"
        title="Previous page"
      />

      {getPages().map((p, i) =>
        p === "…" ? (
          <span key={`ellipsis-${i}`} className="pagination-ellipsis">
            …
          </span>
        ) : (
          <PageBtn
            key={p}
            page={p}
            active={p === currentPage}
            onClick={() => onPageChange(p)}
          />
        ),
      )}

      <NavBtn
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        label="›"
        title="Next page"
      />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────

const ProductLayout = ({
  products = [],
  activeCategory,
  toggleLogin,
  closeCart,
  closeCartRef,
  isCartOpen,
}) => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("All");
  const dispatch = useDispatch();

  const selectedCategory = useSelector(
    (state) => state.filters.selectedCategory,
  );

  useEffect(() => {
    if (location.state?.category) {
      dispatch(setSelectedCategory(location.state.category));
    }
  }, [location.state, dispatch]);
  const shuffledOrder = useSelector((state) => state.shuffle.shuffledOrder);

  const currentPage = useSelector((state) => state.filters.currentPage);

  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();

  const [itemsPerPage] = useState(8);

  useEffect(() => {
    if (products.length) {
      dispatch(ensureShuffledOrder(products.map((p) => p.id)));
    }
  }, [products, dispatch]);

  // apply the stored order to the actual product objects
  const displayedProducts = useMemo(() => {
    if (!shuffledOrder || !products.length) return products;

    const productMap = new Map(products.map((p) => [p.id, p]));
    return shuffledOrder.map((id) => productMap.get(id)).filter(Boolean);
  }, [products, shuffledOrder]);

  // const shuffleArray = (array) => {
  //   const shuffled = [...array];

  //   for (let i = shuffled.length - 1; i > 0; i--) {
  //     const j = Math.floor(Math.random() * (i + 1));

  //     [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  //   }

  //   return shuffled;
  // };

  // const displayedProducts = shuffledOrder
  //   ? shuffledOrder.map((id) => products.find((p) => p.id === id))
  //   : shuffleArray(
  //       Array.isArray(products) ? products : products?.products || [],
  //     );

  const uniqueCategories = displayedProducts
    .map((p) => p.category)
    .filter((c, i, arr) => arr.indexOf(c) === i);

  const uniqueBrands =
    selectedCategory === "All"
      ? displayedProducts
          .map((p) => p.brand)
          .filter((b, i, arr) => arr.indexOf(b) === i)
      : displayedProducts
          .filter((p) => p.category === selectedCategory)
          .map((p) => p.brand)
          .filter((b, i, arr) => arr.indexOf(b) === i);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredProducts = displayedProducts.filter((p) => {
    const searchableFields = [
      p.item_name,
      p.category,
      p.brand,
      p.uploadBy,
      ...(Array.isArray(p.tag) ? p.tag : []),
    ]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());

    const matchesSearch =
      normalizedSearch.length === 0 ||
      searchableFields.some((field) => field.includes(normalizedSearch));

    const matchesCategory =
      selectedCategory === "All" || p.category === selectedCategory;
    const matchesBrand = selectedBrand === "All" || p.brand === selectedBrand;

    return matchesSearch && matchesCategory && matchesBrand;
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / itemsPerPage),
  );

  const safeCurrentPage = Math.min(currentPage, totalPages);

  const handlePageChange = (p) => {
    dispatch(setCurrentPage(Math.max(1, Math.min(p, totalPages))));
  };

  const handleProductClick = (product) => {
    navigate(`/Details/${product.id}`, {
      state: {
        product,
        fromProducts: true,
        page: safeCurrentPage,
        category: selectedCategory,
      },
    });
  };

  const selectedVariant = (product) => {
    if (!product.variants || product.variants.length === 0) {
      return null;
    }

    return product.variants[0];
  };

  const handleAddToCart = (product, selectedVariant) => {
    console.log("handle add product:", product);
    console.log("Selected Variant:", selectedVariant);

    const price = selectedVariant?.price ?? product.price;

    dispatch(
      addToCart({
        ...product,
        price,
        selectedVariant,
      }),
    );
  };
  return (
    <div className="wrapper">
      <div className="container">
        <div className="search-filter-container">
          <div className="search-filter-wrapper-mobile">
            <div className="search-filter">
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
            <div className="mobile-filter-btn">
              <button onClick={() => setShowFilters(!showFilters)}>
                <CiFilter size={28} />
              </button>
            </div>
          </div>
          <div
            className={`filter-container ${
              showFilters ? "filter-container-open" : ""
            }`}
          >
            <div
              className={`brand-container ${
                selectedCategory !== "All" ? "show" : ""
              }`}
            >
              <select
                value={selectedBrand}
                onChange={(e) => {
                  setSelectedBrand(e.target.value);
                  dispatch(setCurrentPage(1));
                }}
              >
                <option value="All">Sub</option>
                {uniqueBrands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </div>

            <div className="category-container">
              <select
                value={selectedCategory}
                onChange={(e) => {
                  dispatch(setSelectedCategory(e.target.value));
                  setSelectedBrand("All");
                  dispatch(setCurrentPage(1));
                }}
              >
                <option value="All">Select Category</option>
                {uniqueCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {isCartOpen && (
          <Cart
            closeCart={closeCart}
            toggleLogin={toggleLogin}
            closeCartRef={closeCartRef}
          />
        )}

        <div className="products-grid">
          {(() => {
            const start = (safeCurrentPage - 1) * itemsPerPage;

            const pageItems = filteredProducts.slice(
              start,
              start + itemsPerPage,
            );

            return pageItems.map((product) => (
              <div key={product.id} className="product-card-list">
                <div
                  className="image-box"
                  onClick={() => handleProductClick(product)}
                >
                  <img
                    src={product.itemPhotos[0]}
                    alt={product.item_name}
                    className="product-image"
                  />
                </div>

                <div className="product-info">
                  <span className="product-category">{product.category}</span>
                  <h3 className="product-name">{product.item_name}</h3>
                  <div className="price-heart">
                    <strong className="product-price">
                      <strong className="product-price">
                        K{product.price ?? product.variants?.[0]?.price ?? 0}
                      </strong>
                    </strong>
                    <HiOutlineShoppingBag
                      className="heart-icon"
                      onClick={() =>
                        handleAddToCart(product, selectedVariant(product))
                      }
                    />
                  </div>
                </div>
              </div>
            ));
          })()}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: 12,
            justifyContent: "center",
            borderTop: "1px solid #eee",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>

        {filteredProducts.length === 0 && (
          <p className="empty">No products found.</p>
        )}
      </div>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default ProductLayout;
